// ⬡B:DC499D0C:FIX:sign_in_never_a_dead_button:20260801⬡
// TM-9. One source for the words a person sees when a sign-in door will not open.
//
// WHY THIS FILE EXISTS. Every sign-in door in this estate called an async sign-in
// function straight out of an onClick with no catch. When the door could not open,
// the promise rejected into nowhere: no navigation, no message, no change on screen.
// A visitor clicked the only button on the page and the page sat there. That is the
// founder's dead-buttons law broken at the front door, and it was broken in six
// separate files because each door wrote its own click handler.
//
// So the classification and the wording live here, once, and every door reads them.
//
// WORDS LAW. Everything exported from this file is read by a stranger who did not
// build this and cannot fix it. No internal vocabulary, no provider names, no
// variable names, no raw error text ever reaches a person. Every message either
// tells them the next thing they can DO, or admits honestly that this one is not
// theirs to fix and says who it belongs to. Nothing here is ever empty.
//
// This module is deliberately plain JavaScript with no React and no JSX so it can be
// imported by the node:test runner directly and locked by a test.

export const ACCOUNT_LOOKUP_PLACEHOLDER = 'email or account code';
export const ACCOUNT_LOOKUP_ACTION = 'Find my account';
export const ACCOUNT_LOOKUP_BUSY = 'Checking…';
export const SIGN_IN_BUSY = 'Opening…';
export const ACCOUNT_LOOKUP_HELP = 'Check whether we know your account. You will still sign in to open it.';
export const ACCOUNT_LOOKUP_FOUND = 'We found your account. Sign in above to open it.';
export const ACCOUNT_LOOKUP_FOUND_NO_SIGN_IN = 'We found your account, but it cannot open here right now. The person who runs this site must restore sign in.';

/**
 * Every sentence a sign-in door is allowed to put on screen.
 *
 * The two `...NoSecondDoor` variants are the honest version for a door that has no
 * other way in: they say plainly that the visitor cannot fix this one, and who can.
 */
export const SIGN_IN_MESSAGES = Object.freeze({
  // The site was never switched on for Google. Nothing the visitor did caused this.
  googleOff:
    'Google sign in isn’t available here right now. Try the email link, or check for your account below.',
  googleOffNoSecondDoor:
    'Google sign in isn’t switched on for this site yet. There’s nothing you can do from here to fix it. The person who runs this site has to switch it on.',

  // Google is switched on but this attempt did not open.
  googleFailed:
    'Google didn’t open just now. Try once more, use the email link, or check for your account below.',
  googleFailedNoSecondDoor:
    'Google didn’t open just now. Give it another try in a moment.',

  // ⬡B:DC499D0C:FIX:the_email_door_speaks_for_itself:20260801⬡
  // The email box had no door of its own, so a failure there was classified as a
  // Google failure and the person who had just typed an address into the email box
  // was told "Google didn’t open just now." Before that it was worse: the sign-in
  // vendor's own validation sentence went straight to the screen, quoting the
  // address back with escaped quotes. Neither is a sentence this product wrote.
  badEmail:
    'That doesn’t look like a complete email address. Check it over and try again.',
  emailLinkOff:
    'Sign in links by email aren’t available here right now. Try Google sign in, or check for your account below.',
  emailLinkOffNoSecondDoor:
    'Sign in links by email aren’t switched on for this site yet. There’s nothing you can do from here to fix it. The person who runs this site has to switch it on.',

  // The visitor typed something we have nobody under.
  noMatch:
    'We don’t have an account under that. Check the spelling, or try another email you may have used.',

  // The visitor's connection, or ours, dropped.
  offline:
    'We couldn’t get through just now. Check your connection and try again.',

  // Last resort. Still a real sentence, still points somewhere.
  unknown:
    'That didn’t go through. Try again, and if it keeps happening the person who runs this site can look into it.',
});

/**
 * Words that must never reach a person on a front end. Exported so a test can lock
 * the whole message set against them instead of trusting a reviewer to notice.
 */
export const SIGN_IN_FORBIDDEN_WORDS = Object.freeze([
  'supabase',
  'auth',
  'oauth',
  'configured',
  'config',
  'env',
  'anon key',
  'token',
  'provider',
  'undefined',
  'null',
  'error',
  'exception',
  'api',
  'endpoint',
  'session',
  'uid',
  'slug',
  'ham',
  // ⬡B:DC499D0C:FIX:first_screen_words:20260801⬡
  // Added after the shared sign-in screen was read on a real phone. Its own label
  // was "quick resolve" and its own placeholder was "email, aclUid, or slug", so the
  // list that was meant to keep internal vocabulary off a screen had never been
  // pointed at the visible copy, only at the failure messages. Both halves of the
  // screen are locked now, and "resolve" is a lookup verb out of this codebase that
  // nobody signing in can act on.
  'acluid',
  'resolve',
  'resolving',
  'no_match',
  'dev sign',
  'straight in',
]);

function messageTextOf(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string') return error.message;
  return '';
}

function reasonOf(error) {
  if (error && error.detail && typeof error.detail.reason === 'string') return error.detail.reason;
  return '';
}

/**
 * Classify a sign-in failure into one of the fixed cases above. Pure, total, and
 * never throws: anything at all in, one of the SIGN_IN_MESSAGES keys out.
 *
 * @param {unknown} error   whatever the sign-in call rejected with, including nothing
 * @param {'google'|'email'|'lookup'} door which door the person was standing at
 * @returns {keyof SIGN_IN_MESSAGES}
 */
export function classifySignInFailure(error, door = 'google') {
  const raw = `${messageTextOf(error)} ${reasonOf(error)}`.toLowerCase();

  if (/no[_ ]?match|not found|no such|unknown identity/.test(raw)) return 'noMatch';
  if (/failed to fetch|networkerror|network error|load failed|econnrefused|timed? ?out/.test(raw)) return 'offline';
  if (door === 'email') {
    // The address itself is checked first. The sign-in vendor reports a malformed
    // address inside its own validation wrapper, so testing for the wrapper before
    // the address would tell a person the site is switched off when what actually
    // happened is a typo they can fix in two seconds.
    if (/invalid|not a valid|malformed|badly formatted/.test(raw)) return 'badEmail';
    if (/not configured|unsupported provider|validation_failed|missing.*key|not available|unavailable|not enabled|disabled/.test(raw)) {
      return 'emailLinkOff';
    }
    return 'unknown';
  }
  if (door === 'lookup') {
    return /account not found|not resolved|resolve failed|invalid/.test(raw) ? 'noMatch' : 'unknown';
  }
  if (/not configured|unsupported provider|validation_failed|missing.*key|not available|unavailable/.test(raw)) {
    return 'googleOff';
  }
  return 'googleFailed';
}

/**
 * The one call every door makes. Returns a sentence that is always non-empty, always
 * written for the person reading it, and never contains the raw failure.
 *
 * @param {unknown} error
 * @param {{ secondDoor?: boolean, door?: 'google'|'email'|'lookup' }} opts
 * @returns {string}
 */
export function signInFailureMessage(error, opts = {}) {
  const { secondDoor = false, door = 'google' } = opts;
  const key = classifySignInFailure(error, door);

  if (!secondDoor && key === 'googleOff') return SIGN_IN_MESSAGES.googleOffNoSecondDoor;
  if (!secondDoor && key === 'googleFailed') return SIGN_IN_MESSAGES.googleFailedNoSecondDoor;
  if (!secondDoor && key === 'emailLinkOff') return SIGN_IN_MESSAGES.emailLinkOffNoSecondDoor;

  return SIGN_IN_MESSAGES[key] || SIGN_IN_MESSAGES.unknown;
}

/**
 * What a door says the moment it renders, before anybody clicks, when it already
 * knows Google cannot open. Returning a sentence here is what stops the estate from
 * painting a button that provably does nothing.
 *
 * @param {{ hasAuth?: boolean, secondDoor?: boolean }} state
 * @returns {string|null} null when the door is fine and should say nothing
 */
export function signInUnavailableNotice({ hasAuth, secondDoor = false } = {}) {
  if (hasAuth) return null;
  return secondDoor
    ? 'Sign in isn’t available here right now. You can check for your account below, but the person who runs this site must restore sign in before it can open.'
    : SIGN_IN_MESSAGES.googleOffNoSecondDoor;
}

/**
 * Turn what a person typed into the hint the backend resolver expects. Kept beside
 * the messages so every door reads one implementation instead of four copies.
 */
export function accountLookupHint(value) {
  const trimmed = String(value == null ? '' : value).trim();
  if (!trimmed) return null;
  if (/^[0-9A-Fa-f]{8}$/.test(trimmed)) return { aclUid: trimmed.toUpperCase() };
  if (trimmed.includes('@')) return { email: trimmed };
  return { slug: trimmed.toLowerCase() };
}
